import * as LiveUpdates from "@capacitor/live-updates";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { Injectable } from "@angular/core";
import { StorageService } from "../storage.service";

@Injectable({
  providedIn: "root",
})
/**
 * Abstraction over Appflow live updates, now through `@capacitor/live-updates` (the old
 * `cordova-plugin-ionic` has no release for Capacitor 8 and its Angular wrapper is a ViewEngine
 * build that Angular 16 cannot load).
 *
 * The new SDK is smaller than the deploy API, and two behaviours around it are worth knowing —
 * both verified against the plugin sources (the JS layer, both native wrappers, and the Android
 * core SDK; the iOS core is a commercial binary, so its semantics are inferred from the shared
 * API and the Android implementation):
 *
 * - `sync()` is also the way to learn the applied snapshot: when the device is up to date it
 *   returns the snapshot currently in use (or none if the device never applied one). There is no
 *   `getCurrentVersion()`, so this service persists the applied snapshot and answers startup
 *   queries from that record.
 * - Applying an update no longer restarts the app by itself (the old `sync('auto')` did):
 *   the new files are staged for the next launch, and an explicit `reload()` restarts now.
 *   The caller decides, which lets it skip the restart when the downloaded build is the one
 *   already compiled into the binary.
 */
export class LiveUpdateService extends StorageService<SnapshotInfo> {

  private get isWebPlatform() { return Capacitor.getPlatform() === "web"; }

  protected getStorageKey(): string { return "appflow_applied_snapshot"; }

  /**
   * Update the channel (and optionally app id) the device checks for updates on. Persisted by the
   * plugin across restarts, but reset to `capacitor.config.ts` values on a new binary — which is
   * fine here, because the caller re-applies the backend-assigned channel before every sync.
   *
   * Never called while the plugin is disabled: with `enabled: false` the Android wrapper skips
   * registering the live update instance at startup, and its `setConfig` then throws an NPE the
   * moment a value actually changes.
   */
  public async configure({ appId, channel }: LiveUpdateTarget): Promise<void> {
    if (this.isWebPlatform) return;
    if ((await this.getConfiguration()).disabled) return;
    await LiveUpdates.setConfig({ appId, channel });
  }

  /**
   * The plugin configuration active on this device. `disabled` mirrors the old plugin's flag:
   * `enabled: false` is set in `capacitor.config.ts` for local development and turned on for
   * store builds, replacing the old `disableDeploy` preference.
   */
  public async getConfiguration(): Promise<LiveUpdateSettings> {
    if (this.isWebPlatform) return {
        appId: "0",
        channel: "NOT APPLICABLE",
        disabled: true,
      };

    const config = await LiveUpdates.getConfig();
    return {
      appId: config?.appId,
      channel: config?.channel,
      disabled: config?.enabled === false,
      updateMethod: config?.autoUpdateMethod,
    };
  }

  /**
   * The snapshot this device last applied, from this service's own record — written on every
   * sync, read back at startup. Like the old `getCurrentVersion()`, this is empty on a fresh
   * install until the first sync completes.
   */
  public async getAppliedSnapshot(): Promise<SnapshotInfo | undefined> {
    if (this.isWebPlatform) return undefined;
    return await this.getObjectFromStorage() || undefined;
  }

  /**
   * Check for an update and download it if there is one. The update is staged, not live:
   * `activeApplicationPathChanged` tells whether the next launch will boot different files, and
   * `reload()` is how the caller makes that happen immediately.
   *
   * Rejects on any failed step (including the plugin being disabled, which is why the caller
   * checks the configuration first) with the SDK's `{ failStep, message }` shape.
   */
  public async sync(progressCallback?: (pct?: number) => void): Promise<SyncOutcome> {
    if (this.isWebPlatform) return { snapshot: undefined, applied: false };

    const result = await LiveUpdates.sync(progressCallback);
    const snapshot = await this.mapAndPersist(result);
    return {
      snapshot,
      applied: result.activeApplicationPathChanged,
    };
  }

  /** Restart the web app on the staged files — the explicit form of the old `sync('auto')`. */
  public async reload(): Promise<void> {
    if (this.isWebPlatform) return;
    await LiveUpdates.reload();
  }

  /**
   * A result carrying a snapshot describes the applied (or still-active) version, so it becomes
   * the record `getAppliedSnapshot()` serves. A result without one means the device runs the
   * binary's own bundle, and any previous record is stale — a snapshot from an older binary.
   */
  private async mapAndPersist(result: LiveUpdates.SyncResult): Promise<SnapshotInfo | undefined> {
    if (!result.snapshot) {
      await this.clearObjectFromStorage();
      return undefined;
    }

    const appInfo = await App.getInfo();
    const snapshot: SnapshotInfo = {
      versionId: result.snapshot.id,
      buildId: result.snapshot.buildId,
      channel: result.liveUpdate.channel,
      binaryVersionName: appInfo.version,
      binaryVersionCode: appInfo.build,
    };
    await this.saveObjectInStorage(snapshot);
    return snapshot;
  }
}

/** What a sync attempt concluded, mapped for the app. */
export interface SyncOutcome {
  /** The applied (or already-active) snapshot; undefined when the device runs the bare binary. */
  snapshot: SnapshotInfo | undefined;
  /** True when the staged files changed, i.e. a reload or the next launch boots this snapshot. */
  applied: boolean;
}

/** Where the device pulls updates from — the part of the configuration the app sets. */
export interface LiveUpdateTarget {
  /** The Appflow app id. */
  appId: string;
  /** The channel the plugin checks for updates on. */
  channel: string;
}

/** The live update configuration active on this device: the target, plus what only it reports. */
export interface LiveUpdateSettings extends LiveUpdateTarget {
  /** Whether live updates are turned off in this build (local development). */
  disabled?: boolean;
  /** The plugin's automatic update behaviour; this app drives updates manually (`none`). */
  updateMethod?: 'none' | 'background';
}

export interface SnapshotInfo {
  /** The id for the snapshot. */
  versionId: string;
  /** The Appflow build the snapshot was produced by. */
  buildId: string;
  /** The channel the snapshot was downloaded for. */
  channel: string;
  /**
   * The binary version name the snapshot was downloaded on —
   * versionName on Android, CFBundleShortVersionString on iOS.
   */
  binaryVersionName: string;
  /**
   * The binary build code the snapshot was downloaded on —
   * versionCode on Android, CFBundleVersion on iOS.
   */
  binaryVersionCode: string;
}
